import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLocation } from '../context/LocationContext';
import { supabase } from '../services/supabase';
import { thumbUrl } from '../utils/imageUrl';
import { compressImage } from '../utils/compressImage';
import LocationBanner from '../components/LocationBanner';

const MenuPage = () => {
  const { business } = useAuth();
  const { locationId } = useLocation();
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [imageUploading, setImageUploading] = useState(false);

  const menuImageInputRef = useRef(null);

  // Category form
  const [newCatName, setNewCatName] = useState('');
  const [editingCat, setEditingCat] = useState(null);

  // Item form modal
  const [itemModal, setItemModal] = useState(null); // null | { categoryId, item? }
  const [itemForm, setItemForm] = useState({
    name: '', description: '',
    name_en: '', description_en: '',
    price: '', is_available: true, target_audience: [], image_url: '',
    variants: [], // [{label, price}]
  });

  const PRESET_SIZES = ['Kiçik', 'Orta', 'Böyük'];

  const addVariant = () =>
    setItemForm(prev => ({ ...prev, variants: [...prev.variants, { label: '', price: '' }] }));

  const updateVariant = (i, field, val) =>
    setItemForm(prev => {
      const v = [...prev.variants];
      v[i] = { ...v[i], [field]: val };
      return { ...prev, variants: v };
    });

  const removeVariant = (i) =>
    setItemForm(prev => ({ ...prev, variants: prev.variants.filter((_, idx) => idx !== i) }));

  const applyPreset = () =>
    setItemForm(prev => ({ ...prev, variants: PRESET_SIZES.map(label => ({ label, price: '' })) }));

  const AUDIENCE_OPTIONS = [
    { id: 'local', label: '🏠 Yerli' },
    { id: 'tourist', label: '✈️ Turist' },
  ];

  const toggleItemAudience = (id) => {
    setItemForm(prev => ({
      ...prev,
      target_audience: prev.target_audience.includes(id)
        ? prev.target_audience.filter(a => a !== id)
        : [...prev.target_audience, id],
    }));
  };

  const showMsg = (msg) => {
    setMessage(msg);
    setTimeout(() => setMessage(''), 3000);
  };

  // ── Load Data ──────────────────────────────────────────────────────────────
  const loadMenu = useCallback(async () => {
    if (!business?.id) return;
    setLoading(true);
    try {
      const baseCatQuery = () => supabase.from('menu_categories').select('*').eq('business_id', business.id).order('sort_order');
      const baseItemQuery = () => supabase.from('menu_items').select('*').eq('business_id', business.id).order('sort_order');

      let [catRes, itemRes] = await Promise.all([
        locationId ? baseCatQuery().eq('location_id', locationId) : baseCatQuery(),
        locationId ? baseItemQuery().eq('location_id', locationId) : baseItemQuery(),
      ]);

      // Retry without location_id filter if column doesn't exist
      if (catRes.error?.code === '42703') {
        catRes = await baseCatQuery();
      }
      if (itemRes.error?.code === '42703') {
        itemRes = await baseItemQuery();
      }

      if (catRes.error) throw catRes.error;
      if (itemRes.error) throw itemRes.error;
      setCategories(catRes.data || []);
      setItems(itemRes.data || []);
    } catch (err) {
      showMsg('❌ Məlumat yüklənmədi: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [business?.id, locationId]);

  useEffect(() => { loadMenu(); }, [loadMenu]);

  // ── Category CRUD ──────────────────────────────────────────────────────────
  const addCategory = async () => {
    if (!newCatName.trim()) return;
    try {
      let { error } = await supabase.from('menu_categories').insert({
        business_id: business.id,
        location_id: locationId || null,
        name: newCatName.trim(),
        sort_order: categories.length,
      });
      // Retry without location_id if column doesn't exist
      if (error?.code === '42703') {
        ({ error } = await supabase.from('menu_categories').insert({
          business_id: business.id,
          name: newCatName.trim(),
          sort_order: categories.length,
        }));
      }
      if (error) throw error;
      setNewCatName('');
      await loadMenu();
      showMsg('✅ Kateqoriya əlavə edildi');
    } catch (err) {
      showMsg('❌ ' + err.message);
    }
  };

  const saveCategory = async (cat) => {
    try {
      const { error } = await supabase
        .from('menu_categories').update({ name: editingCat.name }).eq('id', cat.id);
      if (error) throw error;
      setEditingCat(null);
      await loadMenu();
      showMsg('✅ Kateqoriya yeniləndi');
    } catch (err) {
      showMsg('❌ ' + err.message);
    }
  };

  const deleteCategory = async (catId) => {
    if (!window.confirm('Bu kateqoriyanı və bütün məhsullarını silmək istəyirsiniz?')) return;
    try {
      const { error } = await supabase.from('menu_categories').delete().eq('id', catId);
      if (error) throw error;
      await loadMenu();
      showMsg('✅ Kateqoriya silindi');
    } catch (err) {
      showMsg('❌ ' + err.message);
    }
  };

  // ── Item CRUD ──────────────────────────────────────────────────────────────
  const openAddItem = (categoryId) => {
    setItemForm({ name: '', description: '', name_en: '', description_en: '', price: '', is_available: true, target_audience: [], image_url: '', variants: [] });
    setItemModal({ categoryId, item: null });
  };

  const openEditItem = (item) => {
    setItemForm({
      name: item.name || '',
      description: item.description || '',
      name_en: item.name_en || '',
      description_en: item.description_en || '',
      price: item.price?.toString() || '',
      is_available: item.is_available,
      target_audience: item.target_audience || [],
      image_url: item.image_url || '',
      variants: item.variants || [],
    });
    setItemModal({ categoryId: item.category_id, item });
  };

  // ── Upload menu item image ─────────────────────────────────────────────────
  const handleMenuImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageUploading(true);
    try {
      const compressed = await compressImage(file, { maxWidth: 800, quality: 0.82 });
      const path = `menu/${business.id}/${Date.now()}.jpg`;
      const { error: upErr } = await supabase.storage
        .from('business-photos')
        .upload(path, compressed, { contentType: 'image/jpeg' });
      if (upErr) throw upErr;
      const { data: urlData } = supabase.storage.from('business-photos').getPublicUrl(path);
      setItemForm(prev => ({ ...prev, image_url: urlData.publicUrl }));
    } catch (err) {
      showMsg('❌ Şəkil xətası: ' + err.message);
    } finally {
      setImageUploading(false);
    }
  };

  const saveItem = async () => {
    const hasLocal = itemForm.target_audience.includes('local');
    const hasTourist = itemForm.target_audience.includes('tourist');
    const hasVariants = itemForm.variants.length > 0;
    if (!hasVariants && !itemForm.price) { showMsg('❌ Qiymət və ya ölçü variantı mütləqdir'); return; }
    if (hasVariants && itemForm.variants.some(v => !v.label.trim() || !v.price)) { showMsg('❌ Bütün variantların adı və qiyməti doldurulmalıdır'); return; }
    if (hasLocal && !itemForm.name.trim()) { showMsg('❌ Yerli ad (AZ) mütləqdir'); return; }
    if (hasTourist && !itemForm.name_en.trim()) { showMsg('❌ Turist adı (EN) mütləqdir'); return; }
    if (!hasLocal && !hasTourist) { showMsg('❌ Ən azı bir auditoriya seçin'); return; }

    const cleanVariants = itemForm.variants.map(v => ({ label: v.label.trim(), price: parseFloat(v.price) }));
    const basePrice = hasVariants ? parseFloat(itemForm.variants[0]?.price || 0) : parseFloat(itemForm.price);

    const payload = {
      name: itemForm.name.trim() || itemForm.name_en.trim(),
      description: itemForm.description.trim(),
      name_en: itemForm.name_en.trim() || null,
      description_en: itemForm.description_en.trim() || null,
      price: basePrice,
      variants: cleanVariants,
      is_available: itemForm.is_available,
      target_audience: itemForm.target_audience,
      image_url: itemForm.image_url || null,
    };

    try {
      if (itemModal.item) {
        const { error } = await supabase.from('menu_items').update(payload).eq('id', itemModal.item.id);
        if (error) throw error;
      } else {
        const sortOrder = items.filter(i => i.category_id === itemModal.categoryId).length;
        let { error } = await supabase.from('menu_items').insert({
          business_id: business.id,
          location_id: locationId || null,
          category_id: itemModal.categoryId,
          ...payload,
          sort_order: sortOrder,
        });
        if (error?.code === '42703') {
          ({ error } = await supabase.from('menu_items').insert({
            business_id: business.id,
            category_id: itemModal.categoryId,
            ...payload,
            sort_order: sortOrder,
          }));
        }
        if (error) throw error;
      }
      setItemModal(null);
      await loadMenu();
      showMsg(itemModal.item ? '✅ Məhsul yeniləndi' : '✅ Məhsul əlavə edildi');
    } catch (err) {
      showMsg('❌ ' + err.message);
    }
  };

  const toggleAvailability = async (item) => {
    try {
      const { error } = await supabase.from('menu_items')
        .update({ is_available: !item.is_available }).eq('id', item.id);
      if (error) throw error;
      await loadMenu();
    } catch (err) {
      showMsg('❌ ' + err.message);
    }
  };

  const deleteItem = async (itemId) => {
    if (!window.confirm('Bu məhsulu silmək istəyirsiniz?')) return;
    try {
      const { error } = await supabase.from('menu_items').delete().eq('id', itemId);
      if (error) throw error;
      await loadMenu();
      showMsg('✅ Məhsul silindi');
    } catch (err) {
      showMsg('❌ ' + err.message);
    }
  };

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
      <div style={{ fontSize: 18, color: '#667eea' }}>Menyu yüklənir...</div>
    </div>
  );

  return (
    <div style={{ padding: 24, maxWidth: 900, margin: '0 auto' }}>
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 26, fontWeight: 800, color: '#1A1A2E' }}>🍽️ Menyu İdarəetməsi</h1>
          <p style={{ color: '#888' }}>Kateqoriyalar və məhsullar əlavə edin, redaktə edin</p>
        </div>
        {business?.id && (
          <div style={{ display: 'flex', gap: 10 }}>
            <a
              href={`https://cafegoo.com/menu/${business.id}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '10px 18px', borderRadius: 12,
                background: '#F1F5F9', color: '#1A1A2E',
                textDecoration: 'none', fontWeight: 700, fontSize: 13,
                border: '1px solid #E2E8F0',
              }}
            >
              🔗 Menü Linki
            </a>
            <button
              onClick={() => {
                const menuUrl = `https://cafegoo.com/menu/${business.id}`;
                const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=400x400&data=${encodeURIComponent(menuUrl)}&bgcolor=ffffff&color=1a0f00&margin=20`;
                const a = document.createElement('a');
                a.href = qrUrl;
                a.download = `${business.name || 'menu'}-qr.png`;
                a.target = '_blank';
                a.click();
              }}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '10px 18px', borderRadius: 12,
                background: 'linear-gradient(135deg, #E8940A, #F5A623)',
                color: '#fff', border: 'none', cursor: 'pointer',
                fontWeight: 700, fontSize: 13,
                boxShadow: '0 4px 12px rgba(232,148,10,0.35)',
              }}
            >
              ⬛ QR Kodu Yüklə
            </button>
          </div>
        )}
      </div>
      <LocationBanner />

      {message && (
        <div style={{
          padding: '12px 16px', borderRadius: 10, marginBottom: 20,
          background: message.includes('✅') ? '#e8f5e9' : '#ffebee',
          color: message.includes('✅') ? '#2e7d32' : '#c62828',
          fontWeight: 600,
        }}>{message}</div>
      )}

      {/* ── Add Category ── */}
      <div style={{ background: '#fff', borderRadius: 16, padding: 20, marginBottom: 24,
        boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
        <h3 style={{ marginBottom: 12, fontSize: 16, fontWeight: 700 }}>Yeni Kateqoriya</h3>
        <div style={{ display: 'flex', gap: 10 }}>
          <input
            value={newCatName}
            onChange={e => setNewCatName(e.target.value)}
            placeholder="Kateqoriya adı (məs. Qəhvə, Desert, Əsas Yeməklər)"
            onKeyDown={e => e.key === 'Enter' && addCategory()}
            style={{ flex: 1, padding: '10px 14px', borderRadius: 8, border: '2px solid #e0e0e0',
              fontSize: 14, outline: 'none' }}
          />
          <button onClick={addCategory}
            style={{ padding: '10px 20px', background: '#667eea', color: '#fff', border: 'none',
              borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 14 }}>
            + Əlavə Et
          </button>
        </div>
      </div>

      {/* ── Categories & Items ── */}
      {categories.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#aaa' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🍽️</div>
          <p>Hələ kateqoriya yoxdur. Yuxarıdan əlavə edin.</p>
        </div>
      ) : (
        categories.map(cat => {
          const catItems = items.filter(i => i.category_id === cat.id);
          return (
            <div key={cat.id} style={{ background: '#fff', borderRadius: 16, marginBottom: 20,
              boxShadow: '0 2px 12px rgba(0,0,0,0.08)', overflow: 'hidden' }}>

              {/* Category Header */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '14px 20px', background: '#f8f9ff', borderBottom: '1px solid #eee' }}>
                {editingCat?.id === cat.id ? (
                  <div style={{ display: 'flex', gap: 8, flex: 1 }}>
                    <input value={editingCat.name}
                      onChange={e => setEditingCat({ ...editingCat, name: e.target.value })}
                      onKeyDown={e => e.key === 'Enter' && saveCategory(cat)}
                      style={{ flex: 1, padding: '6px 10px', borderRadius: 6, border: '2px solid #667eea', fontSize: 14 }}
                      autoFocus />
                    <button onClick={() => saveCategory(cat)}
                      style={{ padding: '6px 14px', background: '#27ae60', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>✓</button>
                    <button onClick={() => setEditingCat(null)}
                      style={{ padding: '6px 10px', background: '#e74c3c', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}>✕</button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontWeight: 700, fontSize: 16 }}>{cat.name}</span>
                    <span style={{ fontSize: 12, background: '#667eea20', color: '#667eea',
                      padding: '2px 8px', borderRadius: 20 }}>{catItems.length} məhsul</span>
                  </div>
                )}
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => openAddItem(cat.id)}
                    style={{ padding: '6px 14px', background: '#667eea', color: '#fff', border: 'none',
                      borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                    + Məhsul
                  </button>
                  <button onClick={() => setEditingCat({ id: cat.id, name: cat.name })}
                    style={{ padding: '6px 10px', background: '#f39c12', color: '#fff', border: 'none',
                      borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>✏️</button>
                  <button onClick={() => deleteCategory(cat.id)}
                    style={{ padding: '6px 10px', background: '#e74c3c', color: '#fff', border: 'none',
                      borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>🗑️</button>
                </div>
              </div>

              {/* Items */}
              {catItems.length === 0 ? (
                <div style={{ padding: '20px', color: '#bbb', fontSize: 14, textAlign: 'center' }}>
                  Bu kateqoriyada məhsul yoxdur. "+ Məhsul" düyməsindən əlavə edin.
                </div>
              ) : (
                catItems.map(item => (
                  <div key={item.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '12px 20px', borderBottom: '1px solid #f5f5f5',
                    opacity: item.is_available ? 1 : 0.5 }}>
                    {/* Item image thumbnail */}
                    {item.image_url ? (
                      <img src={thumbUrl(item.image_url)} alt={item.name}
                        style={{ width: 52, height: 52, borderRadius: 10, objectFit: 'cover',
                          border: '2px solid #eee', marginRight: 12, flexShrink: 0 }} />
                    ) : (
                      <div style={{ width: 52, height: 52, borderRadius: 10, background: '#f5f5f5',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        marginRight: 12, flexShrink: 0, fontSize: 22 }}>🍽️</div>
                    )}
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        {item.name && (
                          <span style={{ fontWeight: 600, fontSize: 14 }}>🏠 {item.name}</span>
                        )}
                        {item.name_en && (
                          <span style={{ fontWeight: 600, fontSize: 14, color: '#2563eb' }}>✈️ {item.name_en}</span>
                        )}
                        {!item.is_available && (
                          <span style={{ fontSize: 11, background: '#e74c3c20', color: '#e74c3c',
                            padding: '2px 6px', borderRadius: 10 }}>Mövcud deyil</span>
                        )}
                      </div>
                      {(item.description || item.description_en) && (
                        <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
                          {item.description}{item.description && item.description_en ? ' / ' : ''}{item.description_en}
                        </div>
                      )}
                      {item.target_audience?.length > 0 && (
                        <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                          {item.target_audience.map(a => (
                            <span key={a} style={{ fontSize: 10, background: '#667eea15', color: '#667eea',
                              padding: '1px 6px', borderRadius: 10, fontWeight: 600 }}>
                              {a === 'local' ? '🏠 Yerli' : '✈️ Turist'}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <span style={{ fontWeight: 700, color: '#667eea', fontSize: 15 }}>
                        {item.variants?.length > 0
                          ? `${Math.min(...item.variants.map(v => v.price)).toFixed(2)} – ${Math.max(...item.variants.map(v => v.price)).toFixed(2)} ₼`
                          : `${parseFloat(item.price).toFixed(2)} ₼`}
                      </span>
                      <button onClick={() => toggleAvailability(item)}
                        style={{ padding: '4px 10px', background: item.is_available ? '#27ae60' : '#e74c3c',
                          color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>
                        {item.is_available ? '✅ Aktiv' : '❌ Deaktiv'}
                      </button>
                      <button onClick={() => openEditItem(item)}
                        style={{ padding: '4px 10px', background: '#f39c12', color: '#fff', border: 'none',
                          borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>✏️</button>
                      <button onClick={() => deleteItem(item.id)}
                        style={{ padding: '4px 10px', background: '#e74c3c', color: '#fff', border: 'none',
                          borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>🗑️</button>
                    </div>
                  </div>
                ))
              )}
            </div>
          );
        })
      )}

      {/* ── Item Modal ── */}
      {itemModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 28, width: '100%', maxWidth: 480,
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)', maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={{ marginBottom: 20, fontSize: 18, fontWeight: 700 }}>
              {itemModal.item ? '✏️ Məhsulu Düzəlt' : '+ Yeni Məhsul'}
            </h3>

            {/* Image upload */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 8 }}>Şəkil</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {itemForm.image_url ? (
                  <div style={{ position: 'relative' }}>
                    <img src={thumbUrl(itemForm.image_url)} alt="preview"
                      style={{ width: 72, height: 72, borderRadius: 12, objectFit: 'cover', border: '2px solid #eee' }} />
                    <button onClick={() => setItemForm(prev => ({ ...prev, image_url: '' }))}
                      style={{ position: 'absolute', top: -6, right: -6, background: '#e74c3c', color: '#fff',
                        border: 'none', borderRadius: '50%', width: 20, height: 20, cursor: 'pointer', fontSize: 11 }}>✕</button>
                  </div>
                ) : (
                  <div style={{ width: 72, height: 72, borderRadius: 12, background: '#f5f5f5',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28 }}>🍽️</div>
                )}
                <div>
                  <button onClick={() => menuImageInputRef.current?.click()}
                    disabled={imageUploading}
                    style={{ padding: '8px 16px', background: '#667eea', color: '#fff', border: 'none',
                      borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
                    {imageUploading ? 'Yüklənir...' : '📷 Şəkil Seç'}
                  </button>
                  <p style={{ fontSize: 11, color: '#aaa', marginTop: 4 }}>JPG, PNG — max 5MB</p>
                </div>
              </div>
              <input ref={menuImageInputRef} type="file" accept="image/*" style={{ display: 'none' }}
                onChange={handleMenuImageUpload} />
            </div>

            {/* Audience selector FIRST */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 8 }}>
                Auditoriya * (ən azı birini seçin)
              </label>
              <div style={{ display: 'flex', gap: 8 }}>
                {AUDIENCE_OPTIONS.map(opt => (
                  <div key={opt.id} onClick={() => toggleItemAudience(opt.id)}
                    style={{
                      flex: 1, padding: '10px 12px', borderRadius: 8, cursor: 'pointer',
                      textAlign: 'center', fontSize: 14, fontWeight: 700, userSelect: 'none',
                      border: itemForm.target_audience.includes(opt.id) ? '2px solid #667eea' : '2px solid #e0e0e0',
                      background: itemForm.target_audience.includes(opt.id) ? '#f0f0ff' : '#fafafa',
                      color: itemForm.target_audience.includes(opt.id) ? '#667eea' : '#666',
                      transition: 'all 0.15s',
                    }}>
                    {opt.label}
                  </div>
                ))}
              </div>
            </div>

            {/* Local (AZ) fields */}
            {itemForm.target_audience.includes('local') && (
              <div style={{ marginBottom: 14, padding: 14, background: '#f8fff8', borderRadius: 10, border: '1.5px solid #d1fae5' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#059669', marginBottom: 10 }}>🏠 Yerli — Azərbaycan dilində</div>
                <div style={{ marginBottom: 10 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 5, color: '#374151' }}>Ad (AZ) *</label>
                  <input value={itemForm.name} onChange={e => setItemForm({ ...itemForm, name: e.target.value })}
                    placeholder="Latte, Plov..."
                    style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1.5px solid #d1fae5', fontSize: 14, boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 5, color: '#374151' }}>Təsvir (AZ)</label>
                  <input value={itemForm.description} onChange={e => setItemForm({ ...itemForm, description: e.target.value })}
                    placeholder="Qısa təsvir (ixtiyari)"
                    style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1.5px solid #d1fae5', fontSize: 14, boxSizing: 'border-box' }} />
                </div>
              </div>
            )}

            {/* Tourist (EN) fields */}
            {itemForm.target_audience.includes('tourist') && (
              <div style={{ marginBottom: 14, padding: 14, background: '#eff6ff', borderRadius: 10, border: '1.5px solid #bfdbfe' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#2563eb', marginBottom: 10 }}>✈️ Turist — İngilis dilində</div>
                <div style={{ marginBottom: 10 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 5, color: '#374151' }}>Name (EN) *</label>
                  <input value={itemForm.name_en} onChange={e => setItemForm({ ...itemForm, name_en: e.target.value })}
                    placeholder="Latte, Pilaf..."
                    style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1.5px solid #bfdbfe', fontSize: 14, boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 5, color: '#374151' }}>Description (EN)</label>
                  <input value={itemForm.description_en} onChange={e => setItemForm({ ...itemForm, description_en: e.target.value })}
                    placeholder="Short description (optional)"
                    style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1.5px solid #bfdbfe', fontSize: 14, boxSizing: 'border-box' }} />
                </div>
              </div>
            )}

            {!itemForm.target_audience.includes('local') && !itemForm.target_audience.includes('tourist') && (
              <div style={{ marginBottom: 14, padding: 12, background: '#fef9c3', borderRadius: 8, fontSize: 13, color: '#92400e' }}>
                Auditoriya seçin — sonra ad sahələri görünəcək
              </div>
            )}

            {/* Variants / Price */}
            <div style={{ marginBottom: 14, padding: 14, background: '#f8faff', borderRadius: 10, border: '1.5px solid #e0e7ff' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <label style={{ fontSize: 13, fontWeight: 700, color: '#374151' }}>
                  {itemForm.variants.length > 0 ? '📐 Ölçü variantları' : '💰 Qiymət (₼)'}
                </label>
                <div style={{ display: 'flex', gap: 6 }}>
                  {itemForm.variants.length === 0 && (
                    <button type="button" onClick={applyPreset}
                      style={{ fontSize: 11, padding: '4px 10px', borderRadius: 6, border: '1.5px solid #667eea',
                        background: '#fff', color: '#667eea', cursor: 'pointer', fontWeight: 600 }}>
                      + Kiçik/Orta/Böyük
                    </button>
                  )}
                  <button type="button" onClick={addVariant}
                    style={{ fontSize: 11, padding: '4px 10px', borderRadius: 6, border: '1.5px solid #667eea',
                      background: '#fff', color: '#667eea', cursor: 'pointer', fontWeight: 600 }}>
                    + Variant əlavə et
                  </button>
                </div>
              </div>

              {itemForm.variants.length === 0 ? (
                <input type="number" step="0.01" min="0" value={itemForm.price}
                  onChange={e => setItemForm({ ...itemForm, price: e.target.value })}
                  placeholder="5.50"
                  style={{ width: '100%', padding: '10px 14px', borderRadius: 8,
                    border: '1.5px solid #e0e0e0', fontSize: 14, boxSizing: 'border-box' }} />
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {itemForm.variants.map((v, i) => (
                    <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <input
                        value={v.label}
                        onChange={e => updateVariant(i, 'label', e.target.value)}
                        placeholder="Ölçü adı (məs. Kiçik)"
                        style={{ flex: 1, padding: '8px 12px', borderRadius: 8, border: '1.5px solid #e0e0e0', fontSize: 13, boxSizing: 'border-box' }}
                      />
                      <input
                        type="number" step="0.01" min="0"
                        value={v.price}
                        onChange={e => updateVariant(i, 'price', e.target.value)}
                        placeholder="₼"
                        style={{ width: 80, padding: '8px 10px', borderRadius: 8, border: '1.5px solid #e0e0e0', fontSize: 13, boxSizing: 'border-box' }}
                      />
                      <button type="button" onClick={() => removeVariant(i)}
                        style={{ padding: '6px 10px', background: '#fee2e2', color: '#ef4444', border: 'none',
                          borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 14 }}>✕</button>
                    </div>
                  ))}
                  <button type="button" onClick={() => setItemForm(prev => ({ ...prev, variants: [] }))}
                    style={{ fontSize: 12, color: '#94a3b8', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', padding: 0 }}>
                    ✕ Variantları sil, tək qiymətə qayıt
                  </button>
                </div>
              )}
            </div>

            <div style={{ marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10 }}>
              <input type="checkbox" id="available" checked={itemForm.is_available}
                onChange={e => setItemForm({ ...itemForm, is_available: e.target.checked })}
                style={{ width: 16, height: 16 }} />
              <label htmlFor="available" style={{ fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                Aktiv (sifarişə açıq)
              </label>
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={saveItem}
                style={{ flex: 1, padding: '12px', background: '#667eea', color: '#fff', border: 'none',
                  borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 15 }}>
                {itemModal.item ? '💾 Yadda Saxla' : '✅ Əlavə Et'}
              </button>
              <button onClick={() => setItemModal(null)}
                style={{ padding: '12px 20px', background: '#f0f0f0', color: '#555', border: 'none',
                  borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>Ləğv</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MenuPage;
